import { useCallback } from 'react';
import {
  Button,
  ButtonSizes,
  ButtonVariants,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
} from '@carboledger/ui';
import { TextField } from '@/components/common';
import { useTranslations } from '@/hooks';
import { SHIPPING_SITE_FIELDS, shippingSiteFieldName } from '../constants/suppliers.constants';
import { supplierValidation } from '../suppliersValidation';
import styles from './ShippingSiteFields.module.scss';

/**
 * Props:
 *   siteKey    addresses this card's fields inside the flat form values
 *   position   the card's 1-based position, for its "Shipping site - N" title
 *   onRemove   (siteKey) => void
 *   canRemove  whether Remove is shown
 *   disabled   whether the form is saving
 */
export const ShippingSiteFields = ({ siteKey, position, onRemove, canRemove, disabled }) => {
  const t = useTranslations();
  const handleRemove = useCallback(() => onRemove(siteKey), [onRemove, siteKey]);

  return (
    <div className={styles.site}>
      <div className={styles.header}>
        <Typography
          variant={TypographyVariants.bodySm}
          weight={TypographyWeights.semibold}
          color={TypographyColors.secondary}
        >
          {t('SHIPPING_SITE_CARD_HEADING', { index: position })}
        </Typography>

        {canRemove && (
          <Button
            variant={ButtonVariants.ghost}
            size={ButtonSizes.sm}
            onClick={handleRemove}
            disabled={disabled}
          >
            {t('COMMON_REMOVE')}
          </Button>
        )}
      </div>

      <div className={styles.fields}>
        <TextField
          name={shippingSiteFieldName(siteKey, SHIPPING_SITE_FIELDS.shippingFrom)}
          label={t('FIELD_SHIPPING_FROM')}
          placeholder={t('FORM_SHIPPING_LOCATION_PLACEHOLDER')}
          disabled={disabled}
        />

        <div className={styles.row}>
          <TextField
            name={shippingSiteFieldName(siteKey, SHIPPING_SITE_FIELDS.siteManagerName)}
            label={t('FORM_SITE_MANAGER_NAME_OPTIONAL')}
            placeholder={t('FORM_SITE_MANAGER_NAME_PLACEHOLDER')}
            disabled={disabled}
          />
          <TextField
            name={shippingSiteFieldName(siteKey, SHIPPING_SITE_FIELDS.siteManagerEmail)}
            label={t('FORM_SITE_MANAGER_EMAIL_OPTIONAL')}
            placeholder={t('FORM_SITE_MANAGER_EMAIL_PLACEHOLDER')}
            rules={supplierValidation.email}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};
